import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Form,
  FormGroup,
  Label,
} from 'reactstrap';
import {
  getExamReviewRequestsByExam,
  updateExamReviewRequestStatus,
  resolveExamReviewRequest,
} from '../../../redux/actions/examReviewRequestActions';
import { apiDownload } from '../../../api/index';
import { createErrorMessage, createSuccessMessage, createWarningMessage } from '../../../helpers/notification';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import DataTable from '../../../shared/components/DataTable';
import ModalForm from '../../../shared/components/form/Modal';
import TextBox from '../../../shared/components/form/TextBox';
import { dateTimeFormat } from '../../../helpers/dateFormat';

class ExamReviewRequestsIndex extends PureComponent {
  static propTypes = {
    history: PropTypes.shape({}).isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      items: [],
      showResponseModal: false,
      selectedRequest: null,
      responseStatus: '',
      responseText: '',
    };

    this.columns = [
      {
        key: 'alumno',
        name: 'Alumno',
        search: true,
        format: row => `${row.numeroDocumentoAlumno || ''} - ${row.alumno || ''}`.trim(),
      },
      {
        key: 'estado',
        name: 'Estado',
        search: false,
        format: ({ id, estado }) => (
          <Badge id={`review-status-${id}`} color={this.getStatusColor(estado)}>{this.getStatusLabel(estado)}</Badge>
        ),
      },
      {
        key: 'fechaSolicitud',
        name: 'Fecha solicitud',
        search: false,
        type: 'date',
        format: row => dateTimeFormat(row.fechaSolicitud),
      },
      {
        key: 'motivoReclamo',
        name: 'Motivo',
        search: true,
      },
      {
        key: 'fechaResolucion',
        name: 'Fecha resolución',
        search: false,
        type: 'date',
        format: row => (row.fechaResolucion ? dateTimeFormat(row.fechaResolucion) : '-'),
      },
      {
        key: 'respuestaDocente',
        name: 'Resolución de revisión',
        search: true,
        format: row => row.respuestaDocente || '-',
      },
      {
        key: 'actions',
        name: 'Acción',
        width: '130px',
        search: false,
        format: row => this.renderRowActionButton(row),
      },
    ];
  }

  componentDidMount() {
    this.loadData();
  }

  getStatusLabel = (status) => {
    if (status === 0) return 'Pendiente';
    if (status === 1) return 'En revisión';
    if (status === 2) return 'Resuelta';
    return '-';
  }

  getStatusColor = (status) => {
    if (status === 0) return 'warning';
    if (status === 1) return 'info';
    if (status === 2) return 'success';
    return 'secondary';
  }

  isFinal = status => status === 2;

  isPending = status => status === 0;

  isReviewing = status => status === 1;

  getRowActionLabel = (row) => {
    if (this.isPending(row.estado)) {
      return 'Revisar';
    }

    if (this.isReviewing(row.estado)) {
      return 'Responder';
    }

    return 'Resuelta';
  };

  getRowActionTitle = (row) => {
    if (this.isPending(row.estado)) {
      return 'Pasar a en revisión';
    }

    if (this.isReviewing(row.estado)) {
      return 'Responder solicitud de revisión';
    }

    return 'Solicitud de revisión ya resuelta';
  };

  getRowActionClassName = (row) => {
    if (this.isPending(row.estado)) {
      return 'btn-outline-info';
    }

    if (this.isReviewing(row.estado)) {
      return 'btn-primary';
    }

    return 'btn-outline-secondary';
  };

  renderRowActionButton = row => (
    <button
      type="button"
      className={`btn btn-sm ${this.getRowActionClassName(row)}`}
      title={this.getRowActionTitle(row)}
      onClick={() => this.handleRowAction(row)}
      disabled={this.isFinal(row.estado)}
    >
      {this.getRowActionLabel(row)}
    </button>
  );

  handleRowAction = (row) => {
    if (this.isPending(row.estado)) {
      this.updateStatus(row, 1, {
        reloadData: false,
        onSuccess: () => {
          const updatedRow = { ...row, estado: 1 };
          this.setState(prev => ({
            items: (prev.items || []).map(item => (
              item.id === row.id
                ? { ...item, estado: 1 }
                : item
            )),
          }), () => this.openResponseModal(updatedRow));
        },
      });
      return;
    }

    if (this.isReviewing(row.estado)) {
      this.openResponseModal(row);
      return;
    }

    createWarningMessage('La solicitud de revisión ya está resuelta.');
  };

  openResponseModal = (row) => {
    if (this.isFinal(row.estado)) {
      createWarningMessage('La solicitud de revisión ya está resuelta.');
      return;
    }

    this.setState({
      showResponseModal: true,
      selectedRequest: row,
      // If the request is currently 'En revisión' (1), default the responseStatus to '2' (Resuelta)
      // so the modal opens ready to resolve the request. Otherwise keep current status as string.
      responseStatus: row.estado === 1 ? '2' : `${row.estado}`,
      responseText: row.respuestaDocente || '',
    });
  };

  closeResponseModal = () => {
    this.setState({
      showResponseModal: false,
      selectedRequest: null,
      responseStatus: '',
      responseText: '',
    });
  };

  getAllowedStatuses = (currentStatus) => {
    if (currentStatus === 0) {
      return ['1'];
    }

    if (currentStatus === 1) {
      return ['2'];
    }

    return [];
  };

  isValidStatusTransition = (currentStatus, nextStatus) => this
    .getAllowedStatuses(currentStatus)
    .includes(`${nextStatus}`);

  hasSelectedResult = responseStatus => responseStatus === '1';

  handleResponseTextChange = (event) => {
    const { value } = event.target;
    this.setState({ responseText: value });
  };

  loadData = () => {
    const { id } = this.props;
    this.setState({ loading: true });
    getExamReviewRequestsByExam(id, {
      onSuccess: (items) => {
        this.setState({
          loading: false,
          items: items || [],
        });
      },
      onError: () => {
        this.setState({ loading: false, items: [] });
        createErrorMessage('Error al intentar cargar las solicitud de revisión del examen.');
      },
    });
  };

  updateStatus = (row, estado, options = {}) => {
    const { reloadData = true, onSuccess } = options;

    if (this.isFinal(row.estado)) {
      createWarningMessage('La solicitud de revisión ya está resuelta.');
      return;
    }

    this.setState({ loading: true });
    updateExamReviewRequestStatus(row.id, { estado }, {
      onSuccess: () => {
        createSuccessMessage('Estado de la solicitud de revisión actualizada.');

        if (typeof onSuccess === 'function') {
          onSuccess();
        }

        if (reloadData) {
          this.loadData();
        } else {
          this.setState({ loading: false });
        }
      },
      onError: (error) => {
        this.setState({ loading: false });
        if (error && error.message) {
          createWarningMessage(error.message);
        } else {
          createErrorMessage('Error al intentar actualizar el estado de la solicitud de revisión.');
        }
      },
    });
  };

  resolve = (row, estado) => {
    if (this.isFinal(row.estado)) {
      createWarningMessage('La solicitud de revisión ya está resuelta.');
      return;
    }

    const { responseText } = this.state;
    const respuestaDocente = (responseText || '').trim();
    this.setState({ loading: true });
    resolveExamReviewRequest(row.id, { estado, respuestaDocente }, {
      onSuccess: () => {
        createSuccessMessage('Solicitud de revisión resuelta correctamente.');
        this.closeResponseModal();
        this.loadData();
      },
      onError: (error) => {
        this.setState({ loading: false });
        if (error && error.message) {
          createWarningMessage(error.message);
        } else {
          createErrorMessage('Error al intentar resolver la solicitud de revisión.');
        }
      },
    });
  };

  submitResponse = () => {
    const {
      selectedRequest,
      responseStatus,
      responseText,
    } = this.state;

    if (!selectedRequest) {
      return;
    }

    const estado = parseInt(responseStatus, 10);
    if (Number.isNaN(estado)) {
      createWarningMessage('Debes seleccionar un estado para la solicitud de revisión.');
      return;
    }

    if (!this.isValidStatusTransition(selectedRequest.estado, estado)) {
      createWarningMessage('El flujo es: Pendiente → En revisión → Resuelta.');
      return;
    }

    // only status 2 (Resuelta) requires a response text
    if (estado === 2 && !(responseText || '').trim()) {
      createWarningMessage('Debes ingresar una respuesta para resolver la solicitud de revisión.');
      return;
    }

    if (estado === 0 || estado === 1) {
      this.updateStatus(selectedRequest, estado);
      this.closeResponseModal();
      return;
    }

    this.resolve(selectedRequest, estado);
  };

  onBack = () => {
    const { history, id } = this.props;
    history.push(`/pages/exams/edit/${id}`);
  };

  downloadReviewRequests = () => {
    const { id } = this.props;
    apiDownload(`/api/exams/download-review-requests/${id}`)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `SolicitudesRevision_Exam_${id}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => {
        createErrorMessage('Error al intentar descargar las solicitudes de revisión.');
      });
  };

  getGridActions = () => [];

  render() {
    const {
      loading,
      items,
      showResponseModal,
      selectedRequest,
      responseStatus,
      responseText,
    } = this.state;
    const isPendingSelected = selectedRequest && this.isPending(selectedRequest.estado);
    const isReviewingSelected = selectedRequest && this.isReviewing(selectedRequest.estado);
    const isFinalSelected = responseStatus === '2';
    const showResolveInfo = isReviewingSelected;
    // allow editing when the selected request is in review or when final status is selected
    const isEditable = isReviewingSelected || isFinalSelected;

    if (loading) {
      return <LoadingSpinner />;
    }

    return (
      <Container>
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <h3 className="page-title">Gestión de solicitudes de revisión del examen</h3>
                <DataTable
                  title="Solicitudes de revisión"
                  columns={this.columns}
                  items={items}
                  page={1}
                  pageSize={1000}
                  totalItemsCount={items.length}
                  getGridActions={this.getGridActions}
                  onSubmit={() => { }}
                  onClearFilters={() => { }}
                  initialValues={{}}
                  enableReinitialize
                />

                <div className="card__title" style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={this.downloadReviewRequests}
                  >
                    <i className="fa fa-download" /> Descargar Excel
                  </button>
                </div>

                <div className="card__title">
                  <button type="button" className="btn btn-outline-secondary" onClick={this.onBack}>Volver</button>
                </div>

                <ModalForm
                  header
                  buttonToolbar
                  colored={false}
                  color="primary"
                  title="Responder solicitud de revisión"
                  isOpen={showResponseModal}
                  toggle={this.closeResponseModal}
                  onOk={this.submitResponse}
                  onCancel={this.closeResponseModal}
                  okButtonText="Guardar respuesta"
                  cancelButtonText="Cancelar"
                  // size="lg"
                >
                  <Form className="form" onSubmit={event => event.preventDefault()}>
                    <Row>
                      <Col md={12}>
                        <p>
                          {selectedRequest ? `Alumno: ${selectedRequest.alumno || ''}` : ''}
                        </p>
                        <p>
                          {selectedRequest ? `Motivo: ${selectedRequest.motivoReclamo || ''}` : ''}
                        </p>
                      </Col>
                      {isPendingSelected && (
                        <Col md={12}>
                          <FormGroup>
                            Estado:{' '}
                            <span className="text-info" style={{ marginBottom: '0.5rem' }}>
                              Pendiente
                            </span>
                          </FormGroup>
                        </Col>
                      )}
                      {showResolveInfo && (
                        <Col md={12}>
                          <FormGroup>
                            Estado:{' '}
                            <span className="text-info" style={{ marginBottom: '0.5rem' }}>
                              En revisión
                            </span>
                          </FormGroup>
                        </Col>
                      )}
                      <Col md={12}>
                        <FormGroup>
                          <Label for="responseText">Resolución de revisión</Label>
                          <TextBox
                            input={{
                              name: 'responseText',
                              value: responseText,
                              onChange: this.handleResponseTextChange,
                            }}
                            meta={{}}
                            type="textarea"
                            maxLength={2048}
                            placeholder="Ingrese la resolución de revisión"
                            disabled={!isEditable}
                            style={{ minHeight: '110px' }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </ModalForm>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default ExamReviewRequestsIndex;
